import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import HeaderBread from "../../../components/HeaderBread";
import { useDataLayerValue } from "../../../DataLayer/DataLayer";
import Layout from "../../../Layout";

import { message } from "antd";
import Swal from "sweetalert2";
import CustomHeaderTitle from "../../../components/constants/CustomHeaderTitle";
import DeletePop from "../../../constants/ErrorPermissionModal";

import Logs from "../../../components/Logs";
import SwitchBar from "../../../components/Pages/SwitchBar";
import SearchField from "../../../components/SearchField";
import ActiveSwitchFile from "../../../components/SwitchFile";
import CustomizedTable from "../../../components/UI/CustomizedTable";
import { addlog } from "../../../utils/ApiCalls/Logs";
import { updatePosition } from "../../../utils/ApiCalls/Mobile/ActivityMasters";
import {
  deleteVenueMaster,
  getVenueMaster,
  updateVenueMaster,
  toggleVenueStatus,
} from "../../../utils/ApiCalls/Mobile/VenueMaster";
import { getVessels } from "../../../utils/ApiCalls/SpecialPage";
import { checkPermission } from "../../../utils/Validation/checkPermission";
import { LOG_ACTIONS, LOG_TYPES } from "../../../utils/Constant/LogConstant";

const navigation = [
  {
    title: "Home",
    route: "/",
  },
  {
    title: "Venues",
    route: "/shore/mobile-masters/venue",
  },
];

const VenueMaster = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [vesselOptions, setVesselOptions] = useState([]);
  const [rows, setRows] = useState([]);
  const [active, setActive] = useState("listing");
  const [count, setCount] = useState(0);
  const [{ userDetails, permission }, dispatch] = useDataLayerValue();
  const [columns, setColumns] = useState([]);
  const [sort, setSort] = useState("");
  const [order, setOrder] = useState("");
  const [vesselId, setVesselId] = useState(null);

  const [page, setPage] = useState(
    () => parseInt(new URLSearchParams(location.search).get("page")) || 1
  );
  const [pageSize, setPageSize] = useState(
    () => parseInt(new URLSearchParams(location.search).get("pageSize")) || 10
  );
  const [search, setSearch] = useState(
    () => new URLSearchParams(location.search).get("search") || ""
  );

  const firstLoad = useRef(true);

  // Step 1 — Load params from URL (only on first render) +++++++++++++++++++++++++++++++++++++++++++++++++++++++++
  useEffect(() => {
    if (firstLoad.current) {
      const q = new URLSearchParams(location.search);
      const pageParam = parseInt(q.get("page")) || 1;
      const pageSizeParam = parseInt(q.get("pageSize")) || 10;
      const searchParam = q.get("search") || "";
      const vesselParam = q.get("vesselId");

      setPage(pageParam);
      setPageSize(pageSizeParam);
      setSearch(searchParam);
      if (vesselParam) setVesselId(Number(vesselParam));

      firstLoad.current = false;
    }
  }, [location.search]);

  // Step 2 — Keep URL synced with internal state +++++++++++++++++++++++++++++++++++++++++++++++++++++++++
  useEffect(() => {
    if (firstLoad.current) return;
    const q = new URLSearchParams(location.search);

    const currentParams = {
      page: parseInt(q.get("page")) || 1,
      pageSize: parseInt(q.get("pageSize")) || 10,
      search: q.get("search") || "",
      vesselId: q.get("vesselId") ? Number(q.get("vesselId")) : null,
    };

    // Only navigate if any param changed
    if (
      currentParams.page !== page ||
      currentParams.pageSize !== pageSize ||
      currentParams.search !== search ||
      currentParams.vesselId !== vesselId
    ) {
      q.set("page", page);
      q.set("pageSize", pageSize);
      if (search) q.set("search", search);
      else q.delete("search");
      if (vesselId) q.set("vesselId", vesselId);
      else q.delete("vesselId");
      navigate({ search: q.toString() }, { replace: true });
    }
  }, [page, pageSize, search, vesselId]);

  // Step 3 — Fetch vessels and set default one if not in URL +++++++++++++++++++++++++++++++++++++++++++++++++++++++++
  const getOptionLocation = async () => {
    if (!permission?.permission_json) return;

    dispatch({ type: "SET_LOADING", loading: true });
    let vessels = await getVessels();

    if (vessels.error) {
      Swal.fire({ icon: "error", title: "Oops...", text: vessels.error });
    } else {
      let vesselData = vessels.filter((item) =>
        checkPermission(
          permission,
          "read_per",
          "Categories",
          window?.location?.pathname +
            `/${item.name.toLowerCase().replaceAll(" ", "-")}`
        )
      );
      setVesselOptions(vesselData);

      const q = new URLSearchParams(location.search);
      const urlVesselId = q.get("vesselId");
      const parsedUrlVesselId = urlVesselId ? Number(urlVesselId) : null;
      const hasAllowedUrlVessel =
        parsedUrlVesselId &&
        vesselData.some((item) => item.id === parsedUrlVesselId);
      const resolvedVesselId = hasAllowedUrlVessel
        ? parsedUrlVesselId
        : vesselData?.[0]?.id ?? null;

      if (resolvedVesselId) {
        setVesselId(resolvedVesselId);
      } else {
        setVesselId(null);
      }

      if (!hasAllowedUrlVessel && resolvedVesselId) {
        q.set("vesselId", resolvedVesselId);
        q.set("page", 1);
        q.set("pageSize", pageSize);
        if (search) q.set("search", search);
        else q.delete("search");

        navigate({ search: q.toString() }, { replace: true });
      }
    }

    dispatch({ type: "SET_LOADING", loading: false });
  };

  useEffect(() => {
    getOptionLocation();
  }, [permission]);

  // Step 4 — Fetch category data once vesselId is set +++++++++++++++++++++++++++++++++++++++++++++++++++++++++
  useEffect(() => {
    if (vesselId) getData({ page, pageSize, vesselId, search, sort, order });
  }, [vesselId, page, pageSize, search, sort, order]);

  const getData = async ({ page, pageSize, vesselId, search, sort, order }) => {
    dispatch({ type: "SET_LOADING", loading: true });

    let data = await getVenueMaster({
      page,
      limit: pageSize,
      vessel_id: vesselId,
      search,
      sort,
      order,
    });

    if (data.error) {
      dispatch({
        type: "SET_ERROR_MESSAGE",
        errMess: { message: data.error, type: "error" },
      });
    } else if (data?.venueMasterList) {
      setRows(data.venueMasterList);
      setCount(data.count);
    }

    dispatch({ type: "SET_LOADING", loading: false });
  };

  useEffect(() => {
    setColumns([
      {
        title: "Sl",
        width: 10,
        dataIndex: "id",
        key: "id",
        fixed: "left",
        render: (position, record, index) => (page - 1) * pageSize + index + 1,
      },
      {
        title: "Venue Name",
        dataIndex: "name",
        key: "name",
        sorter: (a, b) => "",
        width: 150,
        render: (item) => <div dangerouslySetInnerHTML={{ __html: item }} />,
      },
      {
        title: "Deck",
        dataIndex: "data",
        key: "deck",
        width: 100,
        render: (dataArray) => (
          <div>
            {dataArray?.length
              ? dataArray.map((d, i) => (
                  <div key={i}>
                    {d.deck_number ? `Deck ${d.deck_number}` : "-"}
                  </div>
                ))
              : "-"}
          </div>
        ),
      },
      {
        title: "Location",
        dataIndex: "data",
        key: "location",
        width: 100,
        render: (dataArray) => (
          <div>
            {dataArray?.length
              ? dataArray.map((d, i) => <div key={i}>{d.location}</div>)
              : "-"}
          </div>
        ),
      },
      {
        title: "Side",
        dataIndex: "data",
        key: "side",
        width: 100,
        render: (dataArray) => (
          <div>
            {dataArray?.length
              ? dataArray.map((d, i) => <div key={i}>{d.side}</div>)
              : "-"}
          </div>
        ),
      },
      {
        title: "Status",
        dataIndex: "is_active",
        key: "is_active",
        width: 30,
        sorter: (a, b) => "",
        render: (item, record, index) => (
          <ActiveSwitchFile
            item={item}
            record={{ ...record, title: record.name }}
            index={index}
            handleUpdate={handleToggle}
          />
        ),
      },
      {
        title: "Action",
        key: "operation",
        width: 100,
        render: (text, record) => (
          <div className="table_action_btn">
            <button className="btn" onClick={() => handleEdit(record.id)}>
              <i
                className="ri-edit-2-line"
                data-toggle="tooltip"
                title="Edit"
              ></i>
            </button>
            <DeletePop onConfirm={() => handleDelete(record.id, record.name, record)}>
              <div>
                {" "}
                <button className="btn delete_btn">
                  <i
                    className="ri-delete-bin-line"
                    data-toggle="tooltip"
                    title="Delete"
                  ></i>
                </button>
              </div>
            </DeletePop>
          </div>
        ),
      },
    ]); // eslint-disable-next-line
  }, [vesselOptions, vesselId, page]);

  const prevVesselRef = useRef(null);

  useEffect(() => {
    // Only reset to 1 if vesselId actually changed by user action
    if (prevVesselRef.current && prevVesselRef.current !== vesselId) {
      setPage(1);
      const q = new URLSearchParams(location.search);
      q.set("page", 1);
      q.set("vesselId", vesselId);
      navigate({ search: q.toString() }, { replace: true });
    }
    prevVesselRef.current = vesselId;
  }, [vesselId]);

  const navigation = [
    {
      title: "Home",
      route: "/",
    },
    {
      title: "Venues",
      route: `/shore/mobile-masters/venue${location.search}`,
    },
  ];

  const sortHandler = async (options) => {
    if (Object.keys(options).length > 0) {
      let sort, order;
      sort = options.columnKey;
      order =
        options.order === "ascend"
          ? "asc"
          : options.order === "descend"
          ? "desc"
          : undefined;
      setSort(sort);
      setOrder(order);
      await getData({ page, pageSize, vesselId, search, sort, order });
    }
  };

  const setPositionalValue = (data) => {};

  const updateBannerPosition = async (banners) => {
    if (
      checkPermission(
        permission,
        "update_per",
        "Venues",
        window?.location?.pathname
      )
    ) {
      try {
        let args = { search: search, page: page, limit: pageSize, vesselId };

        const data = await updatePosition(banners, args);
        addlog("Venue Position Updated", "Venues", {}, userDetails.id);
        if (data.error) {
          Swal.fire({
            icon: "error",
            title: "Oops...",
            text: data.error,
          });
        } else if (data.data) {
          setRows(data.data);
          setCount(data.count);
          setPositionalValue(data.data);
        }
        dispatch({
          type: "SET_LOADING",
          loading: false,
        });
      } catch (error) {
        dispatch({
          type: "SET_LOADING",
          loading: false,
        });

        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: error.message || "Unknown error occurred",
        });
      }
    } else {
      message.error("Update permission missing");
    }
  };

  const handleToggle = async (id, values, title) => {
    if (
      checkPermission(
        permission,
        "update_per",
        "Venues",
        window?.location?.pathname
      )
    ) {
      let data = await toggleVenueStatus(id, vesselId);
      if (data.error) {
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: data.error,
        });
      } else {
        addlog(
          `Venue ${title} ` + (values.is_active === 1 ? LOG_ACTIONS.ACTIVATED : LOG_ACTIONS.DEACTIVATED),
          "Venues",
          { ...values, id, title },
          userDetails.id
        );
      }
      getData({ page, pageSize, vesselId, search, sort, order });
    } else {
      message.error("Update permission missing");
    }
  };

  const handleEdit = (id) => {
    if (
      checkPermission(
        permission,
        "update_per",
        "Venues",
        window?.location?.pathname
      )
    ) {
      const q = new URLSearchParams(location.search);
      q.set("page", page); // always sync current page before navigating
      q.set("pageSize", pageSize);
      if (vesselId) q.set("vesselId", vesselId);
      if (search) q.set("search", search);

      navigate(`/shore/mobile-masters/venue/edit/${id}?${q.toString()}`);
    } else {
      message.error("Update permission missing");
    }
  };

  const handleDelete = async (id,title,record) => {
    if (
      checkPermission(
        permission,
        "delete_per",
        "Venues",
        window?.location?.pathname
      )
    ) {
      await deleteVenueMaster(id, vesselId);
      addlog(LOG_ACTIONS.REMOVED, LOG_TYPES.Venues, { id, title: `${title} Venue` , location_id: vesselId }, userDetails.id);
      await getData({ page, pageSize, vesselId, search, sort, order });
    } else {
      message.error("Missing Permission for delete");
    }
  };

  return (
    <Layout type="read_per" module_data="Venues">
      <CustomHeaderTitle title="Venues" />
      <HeaderBread navigation={navigation} title="Venues" />

      <section className="content" style={{ marginTop: 20 }}>
        <div className="container-fluid">
          <SwitchBar
            tabs={[
              { title: "Venue List", value: "listing" },
              { title: "History Logs", value: "logs" },
            ]}
            activeTab={active}
            setActiveTab={setActive}
          />

          {active === "listing" && (
            <div className="card">
              <div className="card-header two_item">
                <h5 className="card-title">Venue List</h5>
                <Link
                  to={`/shore/mobile-masters/venue/add${location.search}`}
                  className="btn add_v_btn mx-2"
                >
                  Add Venue
                </Link>
              </div>

              <div className="card-body">
                <div className="table_holder">
                  <div className="d-flex align-items-end justify-content-between mb-3">
                    {/* <SwitchBar
                      tabs={vesselOptions?.map((item) => ({
                        title: item.name,
                        value: item.id,
                      }))}
                      activeTab={vesselId}
                      setActiveTab={(val) => {
                        setVesselId(val);

                        setPage(1); //THIS IS NOT STATIC VALUE, IT RESETS THE VALUE OF PAGE TO 1 IF VESSEL CHANGES ++++++++++++

                        const q = new URLSearchParams(location.search);
                        q.set("vesselId", val);
                        q.set("page", 1); // ENSURE URL MATCH ++++++++++++++++++++++++++++++
                        navigate({ search: q.toString() }, { replace: true });
                      }}
                    /> */}
                    <SwitchBar
                      tabs={vesselOptions?.map((item) => ({
                        title: item.name,
                        value: item.id,
                      }))}
                      activeTab={vesselId}
                      setActiveTab={(val) => {
                        setVesselId(val);
                        const q = new URLSearchParams(location.search);
                        q.set("vesselId", val);
                        navigate({ search: q.toString() }, { replace: true });
                      }}
                    />

                    <div style={{ maxWidth: 200 }}>
                      <SearchField
                        value={search}
                        setValue={(e) => setSearch(e.target.value)}
                        SearchValue={() => setPage(page)}
                      />
                    </div>
                  </div>

                  {/* Data Table */}
                  {rows && columns && (
                    <CustomizedTable
                      rows={rows}
                      columns={columns}
                      page={page}
                      pageSize={pageSize}
                      count={count}
                      setRows={setRows}
                      setPage={setPage}
                      setPageSize={setPageSize}
                      sortHandler={sortHandler}
                      updatePosition={updateBannerPosition}
                      title="Crisis Page"
                    />
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Logs */}
          {active === "logs" && (
            <Logs type="Venues" slug="/shore/mobile-masters/venue/edit/" />
          )}
        </div>
      </section>
    </Layout>
  );
};

export default VenueMaster;
